import { useState, useEffect } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip'
import { Mail, Phone, MapPin, ImageIcon, Download, FileText } from 'lucide-react'
import { getCustomerLogoUrl, fetchPartnerLogo } from '@/supabase-client'

export function CustomerInfoSection({ customer, farmLogoUrl, onSpliceDownload, onPriceListDownload }) {
  const [partnerLogoUrl, setPartnerLogoUrl] = useState(null)

  useEffect(() => {
    if (customer?.id) {
      fetchPartnerLogo(customer.id).then(logo => {
        setPartnerLogoUrl(logo?.logo_url_dark || null)
      })
    }
  }, [customer?.id])

  if (!customer) return null

  const customerLogoUrl = partnerLogoUrl || getCustomerLogoUrl(customer.logoFilePath)

  // Build address string from components
  const addressParts = [
    customer.addressLine1,
    customer.addressLine2,
    customer.addressLine3,
    customer.city,
    customer.postcode
  ].filter(Boolean)
  const fullAddress = addressParts.join(', ')

  const canSplice = farmLogoUrl && customerLogoUrl

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          {/* Left: contact details */}
          <div className="flex-1 min-w-0 space-y-3 self-center">
            <h3 className="text-base font-semibold leading-none tracking-tight">Contact Information</h3>
            {customer.email && (
              <div className="flex items-start gap-2 text-sm">
                <Mail className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <a
                  href={`mailto:${customer.email}`}
                  className="text-foreground hover:underline break-all"
                >
                  {customer.email}
                </a>
              </div>
            )}

            {customer.phone && (
              <div className="flex items-start gap-2 text-sm">
                <Phone className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <a
                  href={`tel:${customer.phone}`}
                  className="text-foreground hover:underline"
                >
                  {customer.phone}
                </a>
              </div>
            )}

            {fullAddress && (
              <div className="flex items-start gap-2 text-sm">
                <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <span className="text-foreground">{fullAddress}</span>
              </div>
            )}

            <div className="pt-2 flex flex-wrap items-center gap-3 border-t">
              {customer.reference_code && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Ref:</span>
                  <span className="text-sm font-medium">{customer.reference_code}</span>
                </div>
              )}

              {customer.payment_method && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Payment:</span>
                  <Badge variant="secondary">
                    {customer.payment_method === 'xero' && 'Xero'}
                    {customer.payment_method === 'gocardless' && 'GoCardless'}
                    {customer.payment_method === 'cash' && 'Cash'}
                  </Badge>
                </div>
              )}

              {customer.paymentTermsDays && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Terms:</span>
                  <span className="text-sm font-medium">{customer.paymentTermsDays} days</span>
                </div>
              )}
            </div>
          </div>

          {/* Right: logo + splice button */}
          <div className="flex flex-col items-center gap-2 flex-shrink-0">
            {customerLogoUrl ? (
              <img
                src={customerLogoUrl}
                alt={`${customer.companyName} logo`}
                className="w-64 max-h-64 rounded-lg object-contain ring-1 ring-border bg-white"
              />
            ) : (
              <div className="flex w-64 h-40 items-center justify-center rounded-lg bg-muted">
                <ImageIcon className="h-8 w-8 text-muted-foreground" />
              </div>
            )}
            {canSplice ? (
              <Button
                variant="link"
                size="sm"
                className="h-auto p-0 text-xs"
                onClick={onSpliceDownload}
              >
                <Download className="mr-1 h-3 w-3" />
                Splice Logo
              </Button>
            ) : !farmLogoUrl && customerLogoUrl ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-xs text-muted-foreground cursor-default">Splice Logo</span>
                  </TooltipTrigger>
                  <TooltipContent>Set up your farm logo in Settings first</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : null}
            <Button
              variant="link"
              size="sm"
              className="h-auto p-0 text-xs"
              onClick={onPriceListDownload}
            >
              <FileText className="mr-1 h-3 w-3" />
              Download Price List
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
